"use client";

import { useState } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Textarea, Select, SelectItem, Switch } from "@nextui-org/react";
import { Clock, Upload, Globe, Pencil, Ticket, Users } from "lucide-react";
import { ConnectStripeModal } from "./connect-stripe-modal";
import { CapacityModal } from "./capacity-modal";
import { toast } from "sonner";
import { format } from "date-fns";
import { apiClient } from "@/lib/api-client";

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateEventModal({ isOpen, onClose }: CreateEventModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("11:00 PM");
  const [endTime, setEndTime] = useState<string>("12:00 AM");
  const [image, setImage] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);
  const [requireApproval, setRequireApproval] = useState(false);
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const [isCapacityModalOpen, setIsCapacityModalOpen] = useState(false);
  const [capacity, setCapacity] = useState<number | "unlimited">("unlimited");
  const [hasWaitlist, setHasWaitlist] = useState(false);
  const [timezone, setTimezone] = useState("America/Los_Angeles");

  const timezones = [
    { value: "America/Los_Angeles", label: "GMT-08:00 Los Angeles" },
    { value: "America/New_York", label: "GMT-05:00 New York" },
    { value: "America/Chicago", label: "GMT-06:00 Chicago" },
    { value: "America/Denver", label: "GMT-07:00 Denver" },
    { value: "Europe/London", label: "GMT+00:00 London" },
    { value: "Europe/Paris", label: "GMT+01:00 Paris" },
    { value: "Asia/Tokyo", label: "GMT+09:00 Tokyo" },
    { value: "Asia/Shanghai", label: "GMT+08:00 Shanghai" },
    { value: "Australia/Sydney", label: "GMT+11:00 Sydney" },
  ];

  const generateTimeOptions = (): string[] => {
    const options: string[] = [];
    for (let hour = 0; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const period = ["AM", "PM"];
        period.forEach(p => {
          const h = hour === 0 ? 12 : hour;
          const time = `${h}:${minute.toString().padStart(2, '0')} ${p}`;
          options.push(time);
        });
      }
    }
    return options;
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const formData = new FormData(e.currentTarget);
      
      if (!startDate || !endDate) {
        toast.error("Please select both start and end dates");
        return;
      }

      // Combine date and time
      const startDateTime = new Date(`${startDate} ${startTime}`);
      const endDateTime = new Date(`${endDate} ${endTime}`);

      if (endDateTime <= startDateTime) {
        toast.error("End time must be after start time");
        return;
      }

      const data = {
        name: formData.get("name"),
        description: formData.get("description"),
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        location: formData.get("location"),
        image,
        requireApproval,
        capacity,
        hasWaitlist,
        timezone,
      };

      const response = await apiClient.createEvent(data);

      toast.success("Event created successfully!");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };
const handleImageUpload = (file: File) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    setImage(reader.result as string);
  };
  reader.readAsDataURL(file);
};

const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e?.target?.files?.[0];
  if (file) {
    handleImageUpload(file);
  }
};

const handleDragEnter = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(true);
};

const handleDragLeave = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(false);
};

const handleDragOver = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const handleDrop = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(false);

  const file = e?.dataTransfer?.files?.[0];
  if (file && file?.type?.startsWith('image/')) {
    handleImageUpload(file);
  } else {
    toast.error("Please drop an image file");
  }
};


  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        className="bg-[#1C1B1F] dark"
        scrollBehavior="inside"
        classNames={{
          base: "relative",
          closeButton: "text-white hover:bg-white/20 z-50",
          backdrop: "bg-[#1C1B1F]/50 backdrop-blur-sm",
        }}
      >
        <ModalContent>
          <div className="grid grid-cols-[400px_1fr]">
            <div className="bg-[#2D2C30] p-8 flex items-center justify-center">
              {image ? (
                <img src={image} alt="Event" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <div
                  className={`w-full h-full min-h-[400px] rounded-xl border-2 border-dashed ${
                    isDragging ? 'border-primary bg-primary/10' : 'border-gray-600'
                  } flex flex-col items-center justify-center gap-2 transition-colors`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <Upload className={`h-8 w-8 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
                  <div className="text-center">
                    <Button
                      as="label"
                      htmlFor="image-upload"
                      variant="light"
                      className="cursor-pointer"
                    >
                      Choose a file
                    </Button>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleInputChange}
                      className="hidden"
                      id="image-upload"
                    />
                    <p className="text-sm text-gray-400 mt-2">
                      {isDragging ? 'Drop your image here' : 'or drag and drop your image here'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-8">
              <ModalHeader className="flex flex-col items-start p-0 mb-8">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Globe className="h-4 w-4" />
                  <span>Personal Calendar</span>
                </div>
                <Input
                  id="name"
                  name="name"
                  placeholder="Event Name"
                  variant="bordered"
                  size="lg"
                  className="w-full"
                  classNames={{
                    input: "bg-transparent text-2xl font-semibold placeholder:text-gray-500",
                    inputWrapper: "bg-transparent border-none px-0 h-auto",
                  }}
                  required
                />
              </ModalHeader>

              <form id="event-form" onSubmit={onSubmit} className="space-y-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-2 h-2 rounded-full bg-white" />
                        <span className="text-sm text-gray-400">Start</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e?.target?.value)}
                          min={new Date().toISOString().split('T')[0]}
                          variant="bordered"
                          classNames={{
                            input: "bg-transparent",
                            inputWrapper: "bg-transparent",
                          }}
                        />
                        <Select
                          selectedKeys={[startTime]}
                          onChange={(e) => setStartTime(e?.target?.value)}
                          variant="bordered"
                          classNames={{
                            trigger: "bg-transparent",
                          }}
                        >
                          {generateTimeOptions().map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-2 h-2 rounded-full bg-white" />
                        <span className="text-sm text-gray-400">End</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e?.target?.value)}
                          min={startDate || new Date().toISOString().split('T')[0]}
                          variant="bordered"
                          classNames={{
                            input: "bg-transparent",
                            inputWrapper: "bg-transparent",
                          }}
                        />
                        <Select
                          selectedKeys={[endTime]}
                          onChange={(e) => setEndTime(e?.target?.value)}
                          variant="bordered"
                          classNames={{
                            trigger: "bg-transparent",
                          }}
                        >
                          {generateTimeOptions().map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <Select
                      selectedKeys={[timezone]}
                      onChange={(e) => setTimezone(e?.target?.value)}
                      variant="bordered"
                      size="sm"
                      classNames={{
                        trigger: "bg-transparent h-auto py-0",
                        value: "text-sm text-gray-400",
                      }}
                    >
                      {timezones?.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>

                  <Input
                    id="location"
                    name="location"
                    label="Add Event Location"
                    placeholder="Offline location or virtual link"
                    variant="bordered"
                    classNames={{
                      input: "bg-transparent",
                      inputWrapper: "bg-transparent",
                    }}
                    required
                  />

                  <Textarea
                    id="description"
                    name="description"
                    label="Add Description"
                    variant="bordered"
                    classNames={{
                      input: "bg-transparent",
                      inputWrapper: "bg-transparent",
                    }}
                    required
                  />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Ticket className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">Tickets</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">Free</span>
                        <Button
                          isIconOnly
                          variant="light"
                          size="sm"
                          onPress={() => setIsStripeModalOpen(true)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">Capacity</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{capacity === "unlimited" ? "Unlimited" : capacity}</span>
                        <Button
                          isIconOnly
                          variant="light"
                          size="sm"
                          onPress={() => setIsCapacityModalOpen(true)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm">Require Approval</span>
                      <Switch
                        isSelected={requireApproval}
                        onValueChange={setRequireApproval}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="light"
                    onPress={onClose}
                    isDisabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    type="submit"
                    isDisabled={isLoading}
                  >
                    {isLoading ? "Creating..." : "Create Event"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </ModalContent>
      </Modal>
      <ConnectStripeModal 
        isOpen={isStripeModalOpen}
        onClose={() => setIsStripeModalOpen(false)}
      />
      <CapacityModal
        isOpen={isCapacityModalOpen}
        onClose={() => setIsCapacityModalOpen(false)}
        onSave={(newCapacity, newHasWaitlist) => {
          setCapacity(newCapacity);
          setHasWaitlist(newHasWaitlist);
        }}
        initialCapacity={typeof capacity === "number" ? capacity : undefined}
        initialHasWaitlist={hasWaitlist}
      />
    </>
  );
}
